import { Fragment, type ReactNode } from 'react';

/**
 * Mise en forme du texte des échanges.
 *
 * Le modèle répond en Markdown : sans interprétation, le fil affichait des
 * astérisques et des dièses au milieu des phrases, et une liste de dix points
 * arrivait en un seul paragraphe illisible.
 *
 * Le rendu produit des éléments React et jamais du HTML injecté. C'est
 * volontaire : ce texte vient d'un modèle, lui-même nourri de documents fournis
 * par un tiers, donc il n'a pas à pouvoir poser une balise dans la page. Le
 * sous-ensemble couvert est celui qu'un échange emploie réellement, titres,
 * gras, italique, code, listes, citations et liens.
 */

/** Découpe une ligne en fragments gras, italiques, code et liens. */
function enligne(texte: string, cle: string): ReactNode[] {
    const morceaux: ReactNode[] = [];
    // Un seul balayage, les motifs étant mutuellement exclusifs : le premier qui
    // s'ouvre gagne, ce qui évite d'imbriquer du gras dans du code.
    const motif = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\n]+\*)|(_[^_\n]+_)|(\[[^\]]+\]\((https?:\/\/[^)\s]+)\))/g;
    let dernier = 0;
    let trouve: RegExpExecArray | null = motif.exec(texte);
    let rang = 0;

    while (trouve) {
        if (trouve.index > dernier) morceaux.push(texte.slice(dernier, trouve.index));
        const brut = trouve[0];
        const k = `${cle}-${rang}`;
        rang += 1;

        if (brut.startsWith('`')) {
            morceaux.push(
                <code
                    key={k}
                    className="rounded bg-[var(--color-bg-hover)] px-1 py-0.5 font-mono text-[0.85em]"
                >
                    {brut.slice(1, -1)}
                </code>,
            );
        } else if (brut.startsWith('**')) {
            morceaux.push(<strong key={k} className="font-semibold">{brut.slice(2, -2)}</strong>);
        } else if (brut.startsWith('*') || brut.startsWith('_')) {
            morceaux.push(<em key={k}>{brut.slice(1, -1)}</em>);
        } else {
            const libelle = brut.slice(1, brut.indexOf(']'));
            const cible = trouve[6];
            morceaux.push(
                // `noopener` et `noreferrer` : le lien peut venir d'une source
                // que nous ne contrôlons pas.
                <a
                    key={k}
                    href={cible}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-primary)] underline"
                >
                    {libelle}
                </a>,
            );
        }

        dernier = trouve.index + brut.length;
        trouve = motif.exec(texte);
    }

    if (dernier < texte.length) morceaux.push(texte.slice(dernier));
    return morceaux;
}

type Bloc =
    | { sorte: 'paragraphe'; lignes: string[] }
    | { sorte: 'titre'; niveau: number; texte: string }
    | { sorte: 'liste'; ordonnee: boolean; entrees: string[] }
    | { sorte: 'code'; lignes: string[] }
    | { sorte: 'citation'; lignes: string[] };

/** Regroupe les lignes en blocs, une liste ou un bloc de code étant multiligne. */
function decouper(texte: string): Bloc[] {
    const blocs: Bloc[] = [];
    const lignes = texte.replace(/\r\n/g, '\n').split('\n');
    let dansCode = false;

    for (const ligne of lignes) {
        const nue = ligne.trim();

        if (nue.startsWith('```')) {
            if (dansCode) dansCode = false;
            else { blocs.push({ sorte: 'code', lignes: [] }); dansCode = true; }
            continue;
        }
        const dernier = blocs[blocs.length - 1];
        if (dansCode) {
            if (dernier?.sorte === 'code') dernier.lignes.push(ligne);
            continue;
        }

        if (!nue) { blocs.push({ sorte: 'paragraphe', lignes: [] }); continue; }

        const titre = /^(#{1,4})\s+(.*)$/.exec(nue);
        if (titre) {
            blocs.push({ sorte: 'titre', niveau: titre[1].length, texte: titre[2] });
            continue;
        }

        const puce = /^[-*•]\s+(.*)$/.exec(nue);
        const numero = /^(\d+)[.)]\s+(.*)$/.exec(nue);
        if (puce || numero) {
            const ordonnee = Boolean(numero);
            const entree = puce ? puce[1] : numero![2];
            if (dernier?.sorte === 'liste' && dernier.ordonnee === ordonnee) {
                dernier.entrees.push(entree);
            } else {
                blocs.push({ sorte: 'liste', ordonnee, entrees: [entree] });
            }
            continue;
        }

        const citation = /^>\s?(.*)$/.exec(nue);
        if (citation) {
            if (dernier?.sorte === 'citation') dernier.lignes.push(citation[1]);
            else blocs.push({ sorte: 'citation', lignes: [citation[1]] });
            continue;
        }

        if (dernier?.sorte === 'paragraphe' && dernier.lignes.length > 0) {
            dernier.lignes.push(nue);
        } else if (dernier?.sorte === 'paragraphe') {
            dernier.lignes.push(nue);
        } else {
            blocs.push({ sorte: 'paragraphe', lignes: [nue] });
        }
    }

    // Les blocs vides sont écartés : une ligne blanche ouvre un paragraphe qui
    // peut ne jamais être rempli.
    return blocs.filter((bloc) => {
        if (bloc.sorte === 'titre') return true;
        if (bloc.sorte === 'liste') return bloc.entrees.length > 0;
        return bloc.lignes.length > 0;
    });
}

const TAILLES: Record<number, string> = {
    1: 'text-base font-semibold',
    2: 'text-sm font-semibold',
    3: 'text-sm font-semibold',
    4: 'text-sm font-medium',
};

export function RichText({ children }: { children?: string | null }) {
    if (!children?.trim()) return null;
    const blocs = decouper(children);

    return (
        <div className="space-y-2 text-sm leading-relaxed text-[var(--color-text)]">
            {blocs.map((bloc, index) => {
                const cle = `bloc-${index}`;
                if (bloc.sorte === 'titre') {
                    return (
                        <p key={cle} className={TAILLES[bloc.niveau] || TAILLES[4]}>
                            {enligne(bloc.texte, cle)}
                        </p>
                    );
                }
                if (bloc.sorte === 'code') {
                    return (
                        // Le code déborde en largeur plutôt que de forcer la page
                        // à défiler horizontalement.
                        <pre
                            key={cle}
                            className="overflow-x-auto rounded-lg bg-[var(--color-bg-hover)] p-2
                                font-mono text-xs text-[var(--color-text)]"
                        >
                            {bloc.lignes.join('\n')}
                        </pre>
                    );
                }
                if (bloc.sorte === 'citation') {
                    return (
                        <blockquote
                            key={cle}
                            className="border-l-2 border-[var(--color-border)] pl-3
                                text-[var(--color-text-secondary)]"
                        >
                            {bloc.lignes.map((ligne, rang) => (
                                <Fragment key={`${cle}-${rang}`}>
                                    {enligne(ligne, `${cle}-${rang}`)}
                                    {rang < bloc.lignes.length - 1 && <br />}
                                </Fragment>
                            ))}
                        </blockquote>
                    );
                }
                if (bloc.sorte === 'liste') {
                    const Balise = bloc.ordonnee ? 'ol' : 'ul';
                    return (
                        <Balise
                            key={cle}
                            className={[
                                'space-y-1 pl-5',
                                bloc.ordonnee ? 'list-decimal' : 'list-disc',
                            ].join(' ')}
                        >
                            {bloc.entrees.map((entree, rang) => (
                                <li key={`${cle}-${rang}`}>{enligne(entree, `${cle}-${rang}`)}</li>
                            ))}
                        </Balise>
                    );
                }
                return (
                    <p key={cle}>
                        {bloc.lignes.map((ligne, rang) => (
                            <Fragment key={`${cle}-${rang}`}>
                                {enligne(ligne, `${cle}-${rang}`)}
                                {rang < bloc.lignes.length - 1 && <br />}
                            </Fragment>
                        ))}
                    </p>
                );
            })}
        </div>
    );
}
